import React, { useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { collection, getDocs, doc, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";

// Default plans matching backend config
const DEFAULT_PLANS = [
  {
    id: "books_monthly",
    name: "Ink & Desire",
    description: "For readers who want every word.",
    amount: 499,
    currency: "usd",
    interval: "month",
    features: ["books"],
    accessLevel: 1,
    active: true,
    stripePriceId: "",
  },
  {
    id: "books_yearly",
    name: "Ink & Desire (Yearly)",
    description: "For readers who want every word. Save 15%!",
    amount: 4999,
    currency: "usd",
    interval: "year",
    features: ["books"],
    accessLevel: 1,
    active: true,
    stripePriceId: "",
    discount: 15,
  },
  {
    id: "books_audio_monthly",
    name: "Voices in the Dark",
    description: "For those who want to listen and feel every heartbeat.",
    amount: 1499,
    currency: "usd",
    interval: "month",
    features: ["books", "audio"],
    accessLevel: 2,
    active: true,
    popular: true,
    stripePriceId: "",
  },
  {
    id: "books_audio_yearly",
    name: "Voices in the Dark (Yearly)",
    description: "For those who want to listen and feel every heartbeat. Save 15%!",
    amount: 14999,
    currency: "usd",
    interval: "year",
    features: ["books", "audio"],
    accessLevel: 2,
    active: true,
    popular: true,
    stripePriceId: "",
    discount: 15,
  },
  {
    id: "all_access_monthly",
    name: "All Obsessions",
    description: "For those who want everything—the full Spicify experience.",
    amount: 1999,
    currency: "usd",
    interval: "month",
    features: ["books", "audio", "movies"],
    accessLevel: 3,
    active: true,
    stripePriceId: "",
  },
  {
    id: "all_access_yearly",
    name: "All Obsessions (Yearly)",
    description: "For those who want everything. Save 15%!",
    amount: 19999,
    currency: "usd",
    interval: "year",
    features: ["books", "audio", "movies"],
    accessLevel: 3,
    active: true,
    stripePriceId: "",
    discount: 15,
  },
];

const FeatureBadge = ({ feature }) => {
  const styles = {
    books: "bg-blue-100 text-blue-800",
    audio: "bg-purple-100 text-purple-800",
    movies: "bg-red-100 text-red-800",
  };
  const icons = { books: "📖", audio: "🎧", movies: "🎥" };
  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[feature] || "bg-gray-100 text-gray-800"}`}>
      {icons[feature] || "•"} {feature}
    </span>
  );
};

export default function SubscriptionPlans() {
  const { currentUser } = useAuth();
  const auth = getAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [subscriberStats, setSubscriberStats] = useState({ total: 0, active: 0, canceled: 0, pastDue: 0 });
  const [canManagePlans, setCanManagePlans] = useState(false);

  useEffect(() => {
    fetchPlans();
    fetchSubscriberStats();
  }, []);

  useEffect(() => {
    const loadAdminAccess = async () => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        setCanManagePlans(false);
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        const isAdminClaim = tokenResult?.claims?.admin === true;
        setCanManagePlans(isAdminClaim || currentUser?.role === "admin");
      } catch (error) {
        console.error("Failed to read admin claims:", error);
        setCanManagePlans(currentUser?.role === "admin");
      }
    };

    loadAdminAccess();
  }, [auth, currentUser]);

  const requireAdminAction = () => {
    if (!canManagePlans) {
      toast.error("Admin access required");
      return false;
    }
    return true;
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, "subscriptionPlans"));
      
      if (snapshot.empty) {
        // Initialize with default plans
        for (const plan of DEFAULT_PLANS) {
          await setDoc(doc(db, "subscriptionPlans", plan.id), plan);
        }
        setPlans(DEFAULT_PLANS);
      } else {
        const planList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPlans(planList.sort((a, b) => a.accessLevel - b.accessLevel || (a.interval === "month" ? -1 : 1)));
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Failed to load plans");
      setPlans(DEFAULT_PLANS);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriberStats = async () => {
    try {
      const usersSnap = await getDocs(collection(db, "users"));
      let stats = { total: 0, active: 0, canceled: 0, pastDue: 0 };
      usersSnap.forEach((d) => {
        const data = d.data();
        if (data.subscription) {
          stats.total++;
          if (data.subscription.status === "active") stats.active++;
          else if (data.subscription.status === "canceled") stats.canceled++;
          else if (data.subscription.status === "past_due") stats.pastDue++;
        }
      });
      setSubscriberStats(stats);
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
  };

  const togglePlanActive = async (planId, currentState) => {
    if (!requireAdminAction()) return;
    try {
      await setDoc(doc(db, "subscriptionPlans", planId), { active: !currentState }, { merge: true });
      setPlans((prev) => prev.map((p) => (p.id === planId ? { ...p, active: !currentState } : p)));
      toast.success(`Plan ${!currentState ? "activated" : "deactivated"}`);
    } catch (error) {
      console.error("Error toggling plan:", error);
      toast.error("Failed to update plan");
    }
  };

  const openEditModal = (plan) => {
    setEditingPlan({ ...plan });
    setShowEditModal(true);
  };

  const savePlan = async () => {
    if (!requireAdminAction()) return;
    if (!editingPlan) return;

    try {
      await setDoc(doc(db, "subscriptionPlans", editingPlan.id), editingPlan, { merge: true });
      setPlans((prev) => prev.map((p) => (p.id === editingPlan.id ? editingPlan : p)));
      setShowEditModal(false);
      setEditingPlan(null);
      toast.success("Plan updated successfully");
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error("Failed to save plan");
    }
  };

  const formatPrice = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-600">Loading subscription plans...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-2 text-gray-900">Subscription Plans</h1>
      <p className="text-gray-500 mb-6">Manage your subscription tiers and Stripe pricing</p>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <p className="text-sm text-gray-500">Total Subscribers</p>
          <p className="text-2xl font-bold text-gray-900">{subscriberStats.total}</p>
        </div>
        <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
          <p className="text-sm text-green-600">Active</p>
          <p className="text-2xl font-bold text-green-700">{subscriberStats.active}</p>
        </div>
        <div className="bg-white border border-orange-200 rounded-lg p-4 shadow-sm">
          <p className="text-sm text-orange-600">Canceled</p>
          <p className="text-2xl font-bold text-orange-700">{subscriberStats.canceled}</p>
        </div>
        <div className="bg-white border border-red-200 rounded-lg p-4 shadow-sm">
          <p className="text-sm text-red-600">Past Due</p>
          <p className="text-2xl font-bold text-red-700">{subscriberStats.pastDue}</p>
        </div>
      </div>

      {/* Plans Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-4 text-gray-600 font-medium">Plan</th>
                <th className="p-4 text-gray-600 font-medium">Price</th>
                <th className="p-4 text-gray-600 font-medium">Interval</th>
                <th className="p-4 text-gray-600 font-medium">Features</th>
                <th className="p-4 text-gray-600 font-medium">Access Level</th>
                <th className="p-4 text-gray-600 font-medium">Stripe Price ID</th>
                <th className="p-4 text-gray-600 font-medium">Status</th>
                {canManagePlans && <th className="p-4 text-gray-600 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id} className="border-b hover:bg-gray-50 transition-colors">
                  <td className="p-4">
                    <div>
                      <p className="font-semibold text-gray-900">{plan.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                      {plan.popular && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">⭐ Popular</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 font-semibold text-gray-900">
                    {formatPrice(plan.amount)}
                    {plan.discount && (
                      <span className="ml-1 text-xs text-green-600">-{plan.discount}%</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      plan.interval === "month" ? "bg-blue-50 text-blue-700" : "bg-indigo-50 text-indigo-700"
                    }`}>
                      {plan.interval === "month" ? "Monthly" : "Yearly"}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {(plan.features || []).map((f) => (
                        <FeatureBadge key={f} feature={f} />
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                      Tier {plan.accessLevel}
                    </span>
                  </td>
                  <td className="p-4">
                    {plan.stripePriceId ? (
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700 break-all">
                        {plan.stripePriceId}
                      </code>
                    ) : (
                      <span className="text-xs text-red-500 font-medium">⚠️ Not configured</span>
                    )}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => togglePlanActive(plan.id, plan.active)}
                      className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                        plan.active
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-red-100 text-red-800 hover:bg-red-200"
                      }`}
                      disabled={!canManagePlans}
                    >
                      {plan.active ? "✅ Active" : "❌ Inactive"}
                    </button>
                  </td>
                  {canManagePlans && (
                    <td className="p-4">
                      <button
                        onClick={() => openEditModal(plan)}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-yellow-800 mb-3">⚡ Stripe Setup Instructions</h3>
        <ol className="text-sm text-yellow-700 space-y-2 list-decimal list-inside">
          <li>Go to <strong>Stripe Dashboard → Products</strong></li>
          <li>Create 3 products: <strong>Ink & Desire</strong>, <strong>Voices in the Dark</strong>, <strong>All Obsessions</strong></li>
          <li>For each product, create <strong>Monthly</strong> and <strong>Yearly</strong> price variants</li>
          <li>Copy each <strong>Price ID</strong> (starts with <code className="bg-yellow-100 px-1 rounded">price_</code>)</li>
          <li>Click <strong>Edit</strong> on each plan above and paste the Stripe Price ID</li>
          <li>Set your <strong>Stripe Secret Key</strong> and <strong>Webhook Secret</strong> in your backend environment variables</li>
        </ol>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Plan: {editingPlan.name}</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                  <input
                    type="text"
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                    className="w-full p-2 border rounded-lg text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={editingPlan.description}
                    onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
                    className="w-full p-2 border rounded-lg text-gray-900"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (cents)</label>
                    <input
                      type="number"
                      value={editingPlan.amount}
                      onChange={(e) => setEditingPlan({ ...editingPlan, amount: parseInt(e.target.value) || 0 })}
                      className="w-full p-2 border rounded-lg text-gray-900"
                    />
                    <p className="text-xs text-gray-500 mt-1">= {formatPrice(editingPlan.amount)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Interval</label>
                    <select
                      value={editingPlan.interval}
                      onChange={(e) => setEditingPlan({ ...editingPlan, interval: e.target.value })}
                      className="w-full p-2 border rounded-lg text-gray-900"
                    >
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stripe Price ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingPlan.stripePriceId || ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, stripePriceId: e.target.value })}
                    placeholder="price_1234567890..."
                    className="w-full p-2 border rounded-lg text-gray-900 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Get this from Stripe Dashboard → Products → Price ID
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Access Level</label>
                  <select
                    value={editingPlan.accessLevel}
                    onChange={(e) => setEditingPlan({ ...editingPlan, accessLevel: parseInt(e.target.value) })}
                    className="w-full p-2 border rounded-lg text-gray-900"
                  >
                    <option value={1}>Level 1 — Books Only</option>
                    <option value={2}>Level 2 — Books + Audio</option>
                    <option value={3}>Level 3 — All Access (Books + Audio + Movies)</option>
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={editingPlan.active}
                    onChange={(e) => setEditingPlan({ ...editingPlan, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label className="text-sm text-gray-700">Plan is active and available for purchase</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={editingPlan.popular || false}
                    onChange={(e) => setEditingPlan({ ...editingPlan, popular: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label className="text-sm text-gray-700">Mark as "Popular" plan</label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={savePlan}
                  className="flex-1 bg-black text-white py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => { setShowEditModal(false); setEditingPlan(null); }}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

